import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonControl, FieldControl, IconControl } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import { Operations } from "../../../../../services/ItemServices";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import {
  RESOURCES_BASE_PATH,
  buildResourceConfigurationManagePath,
  buildResourceConfigurationWorkspacePath,
  buildResourceConfigurationWorkspaceScreenPath
} from "../../../resourceRoutes";
import {
  buildOperationPayload,
  buildResourceOperationQueryParams,
  buildServerFailureError,
  copyTextToClipboard,
  extractValidationIssues,
  getCurrentSectionStepKey,
  getCurrentStepKey,
  getErrorSummary,
  getOperationResult,
  getOperationItem,
  getSectionStepLabel,
  getSectionStepKey,
  getStepKey,
  getValidationIssueSummary,
  getRunSectionStepInputValues,
  getValue,
  formatCount,
  isCompletedStatus,
  stepKeysEqual,
  requireSuccessfulServiceResponse
} from "../resourceConfigWizardUtils";
import {
  DESTINATION_KIND_WAREHOUSE,
  buildDestinationConnectionCheckMetadata,
  getDestinationCheckOperationName,
  getDestinationKind,
  getDestinationManagerKey,
  getDestinationSetupDefaults,
  getDeploymentRecordId,
  loadDestinationById
} from "./destinationSetupStepUtils";

const VALIDATION_STATE_FIELD = "__validationState";
const REVIEW_RECORD_BATCH_SIZE = 12;
const URL_FACT_LABEL_PATTERN = /\burl\b/i;
const ENTITY_OBJECTS_FACT_LABEL = "entity objects";

const normalizeSeverityClass = (severity) =>
  String(severity || "Error").trim().toLowerCase() === "warning" ? "warning" : "error";

const issueMessage = (issue) => getValue(issue, "Message", "message", "DisplayMessage", "displayMessage") || "";

const isReviewUrlValue = (value) => {
  const text = String(value || "").trim();
  return text.startsWith("http://")
    || text.startsWith("https://")
    || text.startsWith("/");
};

const isReviewUrlFact = (fact) =>
  URL_FACT_LABEL_PATTERN.test(getValue(fact, "Label", "label") || "")
  && isReviewUrlValue(getValue(fact, "Value", "value"));

const isEntityObjectsFact = (fact) =>
  String(getValue(fact, "Label", "label") || "").trim().toLowerCase() === ENTITY_OBJECTS_FACT_LABEL;

const parseEntityObjectNames = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry || "").trim()).filter(Boolean);
  }
  return String(value || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const EntityObjectCardList = ({ fact }) => {
  const entityObjectNames = parseEntityObjectNames(getValue(fact, "Value", "value"));
  if (!entityObjectNames.length) return null;

  return (
    <div className="resource-wizard__entity-object-list">
      <span>{getValue(fact, "Label", "label")}</span>
      <div className="resource-wizard__entity-object-cards">
        {entityObjectNames.map((name) => (
          <article className="resource-wizard__entity-object-card" key={name}>
            <strong>{name}</strong>
          </article>
        ))}
      </div>
    </div>
  );
};

const ReviewSummaryFact = ({ fact }) => {
  const label = getValue(fact, "Label", "label");
  const value = getValue(fact, "Value", "value");

  if (isReviewUrlFact(fact)) {
    const textValue = String(value || "").trim();
    return (
      <div className="resource-wizard__info-pair resource-wizard__info-pair--url-action">
        <span>{label}</span>
        <ButtonControl
          type="standard-small"
          className="resource-wizard__url-copy-button"
          aria-label={`Copy ${label}`}
          title={textValue}
          onClick={() => copyTextToClipboard(textValue)}
        >
          <IconControl iconKey="clone" title={textValue} width="1rem" height="1rem" />
        </ButtonControl>
      </div>
    );
  }

  return (
    <div className="resource-wizard__info-pair">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
};

const filterIssuesForStep = (issues, stepKey, sectionStepKey) =>
  issues.filter((issue) =>
    stepKeysEqual(getValue(issue, "StepKey", "stepKey"), stepKey)
    && stepKeysEqual(getValue(issue, "SectionStepKey", "sectionStepKey"), sectionStepKey));

const extractIssuesForStep = (source, stepKey, sectionStepKey) =>
  filterIssuesForStep(extractValidationIssues(source), stepKey, sectionStepKey);

const getStoredValidationState = (values = {}) => getValue(values, VALIDATION_STATE_FIELD, "validationState") || {};

const buildValidationStateValues = (values, issues) => ({
  ...(values || {}),
  [VALIDATION_STATE_FIELD]: {
    hasRun: true,
    issues
  }
});

const isDestinationValidationStep = (context = {}) =>
  String(getValue(context.sectionStep, "UiPatternKey", "uiPatternKey") || "").trim().toLowerCase() === "destinationvalidation";

const firstNonEmpty = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";

const readFirstArrayValue = (value) => Array.isArray(value) ? value.find(Boolean) || "" : value;

const buildWarehouseValidationCheckValues = async ({
  context,
  values,
  resourceDomainContext,
  coreState
}) => {
  if (!isDestinationValidationStep(context)) return values || {};

  const defaults = getDestinationSetupDefaults(context.wizard, context.productResourceDefaults);
  if (getDestinationKind(defaults) !== DESTINATION_KIND_WAREHOUSE) return values || {};

  const destinationMgr = resourceDomainContext?.managers?.[getDestinationManagerKey(defaults)];
  if (!destinationMgr?.RunOperation) return values || {};

  const operationName = getDestinationCheckOperationName(defaults);
  if (!operationName) return values || {};

  const deploymentValues = getRunSectionStepInputValues(context.wizard, "configure", "deployment");
  const destinationValues = getRunSectionStepInputValues(context.wizard, "configure", "destination");
  const destinationId = firstNonEmpty(
    getValue(values, "DestinationId", "destinationId"),
    getValue(deploymentValues, "DestinationId", "destinationId"),
    getValue(destinationValues, "DestinationId", "destinationId"),
    getValue(context.wizard, "ResourceConfiguration.LinkedRecords.0.ItemId")
  );
  const deploymentId = firstNonEmpty(
    getValue(values, "DestinationConnectionCheckDeploymentId", "ConnectionCheckDeploymentId"),
    getValue(deploymentValues, "DestinationConnectionCheckDeploymentId", "ConnectionCheckDeploymentId"),
    readFirstArrayValue(getValue(deploymentValues, "DeploymentIds", "deploymentIds"))
  );

  if (!destinationId || !deploymentId) return values || {};

  const deployment = { UniqueId: deploymentId };
  let destination = { _id: destinationId, UniqueId: destinationId };
  try {
    destination = await loadDestinationById({
      manager: destinationMgr,
      destinationId,
      coreState,
      fallbackMessage: "Destination could not be loaded for validation."
    }) || destination;
  } catch {
    destination = { _id: destinationId, UniqueId: destinationId };
  }

  try {
    const queryParams = buildResourceOperationQueryParams({
      wizard: context.wizard,
      item: destination,
      queryParams: buildDestinationConnectionCheckMetadata({
        destination,
        deployment,
        defaults
      })
    });
    const result = await destinationMgr.RunOperation({
      operationName,
      item: destination,
      queryParams,
      isItem: true,
      isWarning: true,
      supressAllErrors: false
    });
    requireSuccessfulServiceResponse(result, "Connection check failed.", coreState);
    const operationResult = getOperationResult(result);
    const returnedDeployment = getOperationItem(result);
    const checkedDeploymentId = getDeploymentRecordId(returnedDeployment) || deploymentId;
    return {
      ...(values || {}),
      DestinationConnectionCheckStatus: "Success",
      DestinationConnectionCheckMessage: getValue(operationResult, "DisplayMessage", "displayMessage")
        || getValue(result, "DisplayMessage", "displayMessage")
        || "Connection check completed.",
      DestinationConnectionCheckedUtc: new Date().toISOString(),
      DestinationConnectionCheckDeploymentId: checkedDeploymentId,
      DestinationConnectionCheckDeploymentName: getValue(returnedDeployment, "SystemName", "systemName", "SchemaDestinationName", "schemaDestinationName", "DBName", "dbName") || ""
    };
  } catch (ex) {
    return {
      ...(values || {}),
      DestinationConnectionCheckStatus: "Failed",
      DestinationConnectionCheckMessage: getErrorSummary(ex, "Connection check failed."),
      DestinationConnectionCheckedUtc: new Date().toISOString(),
      DestinationConnectionCheckDeploymentId: deploymentId
    };
  }
};

export const ValidationStep = ({
  context,
  onSave,
  onContinue,
  registerStepController
}) => {
  const resourceDomainContext = useResourceDomainContext();
  const managerRef = useRef(resourceDomainContext?.managers?.resourceConfigurationWizard);
  const coreStateRef = useRef(resourceDomainContext?.coreState);
  const resourceDomainContextRef = useRef(resourceDomainContext);
  const contextRef = useRef(context);
  const stepKey = getStepKey(context.step);
  const sectionStepKey = getSectionStepKey(context.sectionStep);
  const storedValidationState = getStoredValidationState(context.values);
  const runSectionStepStatus = getValue(context.runSectionStep, "StepStatus", "stepStatus", "Status", "status");
  const initialIssues = filterIssuesForStep(storedValidationState.issues || [], stepKey, sectionStepKey);
  const [issues, setIssues] = useState(() =>
    initialIssues.length ? initialIssues : extractIssuesForStep(context.wizard, stepKey, sectionStepKey));
  const [hasValidated, setHasValidated] = useState(() =>
    Boolean(storedValidationState.hasRun)
    || initialIssues.length > 0
    || extractIssuesForStep(context.wizard, stepKey, sectionStepKey).length > 0
    || isCompletedStatus(runSectionStepStatus));
  const [validating, setValidating] = useState(false);
  const validatingRef = useRef(false);

  useEffect(() => {
    managerRef.current = resourceDomainContext?.managers?.resourceConfigurationWizard;
    coreStateRef.current = resourceDomainContext?.coreState;
    resourceDomainContextRef.current = resourceDomainContext;
  }, [resourceDomainContext, resourceDomainContext?.coreState, resourceDomainContext?.managers?.resourceConfigurationWizard]);

  useEffect(() => {
    contextRef.current = context;
  }, [context]);

  useEffect(() => {
    const nextStoredState = getStoredValidationState(context.values);
    const nextStoredIssues = filterIssuesForStep(nextStoredState.issues || [], stepKey, sectionStepKey);
    const nextIssues = nextStoredIssues.length
      ? nextStoredIssues
      : extractIssuesForStep(context.wizard, stepKey, sectionStepKey);
    setIssues(nextIssues);
    setHasValidated(
      Boolean(nextStoredState.hasRun)
      || nextIssues.length > 0
      || isCompletedStatus(getValue(context.runSectionStep, "StepStatus", "stepStatus", "Status", "status"))
    );
  }, [context.runSectionStep, context.values, context.wizard, sectionStepKey, stepKey]);

  const runValidation = useCallback(async (values) => {
    const currentContext = contextRef.current;
    const manager = managerRef.current;
    if (!manager?.RunOperation) throw new Error("Resource Configuration validation manager is not available.");

    setValidating(true);
    try {
      const wizard = currentContext.wizard;
      validatingRef.current = true;
      const validationValues = await buildWarehouseValidationCheckValues({
        context: currentContext,
        values,
        resourceDomainContext: resourceDomainContextRef.current,
        coreState: coreStateRef.current
      });
      const payload = buildOperationPayload(wizard, {
        CurrentStepKey: getCurrentStepKey(wizard),
        CurrentSectionStepKey: getCurrentSectionStepKey(wizard, getCurrentStepKey(wizard)),
        StepKey: getStepKey(currentContext.step),
        SectionStepKey: getSectionStepKey(currentContext.sectionStep),
        StepInputValuesJson: JSON.stringify(validationValues || {})
      });
      const result = await manager.RunOperation({
        operationName: Operations.ResourceConfigurationWizardItem.ResourceConfigurationWizardValidate,
        item: payload,
        queryParams: buildResourceOperationQueryParams({
          wizard,
          item: payload
        }),
        isItem: true,
        isWarning: true,
        supressAllErrors: true
      });
      if (!result) throw new Error("Resource validation did not return a response. Sign in again and retry.");
      const operationResult = getOperationResult(result);
      const failed = getValue(result, "Success", "success") === false
        || getValue(operationResult, "Success", "success") === false;
      const nextIssues = extractIssuesForStep(result, getStepKey(currentContext.step), getSectionStepKey(currentContext.sectionStep));
      setIssues(nextIssues);
      setHasValidated(true);
      if (failed) throw buildServerFailureError(operationResult || result, "Resource validation failed.", coreStateRef.current);
      return { values: validationValues, issues: nextIssues };
    } catch (ex) {
      const nextIssues = ex?.validationIssues || [];
      if (nextIssues.length) setIssues(nextIssues);
      setHasValidated(true);
      throw ex;
    } finally {
      validatingRef.current = false;
      setValidating(false);
    }
  }, []);

  useEffect(() => {
    if (!registerStepController) return undefined;
    registerStepController({
      skipStepInputValidation: true,
      get isBusy() {
        return Boolean(validatingRef.current);
      },
      prepareSave: async (values) => {
        const validationResult = await runValidation(values);
        return buildValidationStateValues(validationResult.values, validationResult.issues);
      },
      prepareContinue: async (values) => {
        const validationResult = await runValidation(values);
        const nextIssues = validationResult.issues;
        const hasError = nextIssues.some((issue) => normalizeSeverityClass(getValue(issue, "Severity", "severity")) === "error");
        if (hasError) {
          const error = new Error(getValidationIssueSummary(nextIssues) || "Resource validation failed.");
          error.validationIssues = nextIssues;
          throw error;
        }
        return buildValidationStateValues(validationResult.values, nextIssues);
      }
    });
    return () => registerStepController(null);
  }, [registerStepController, runValidation]);

  const warningCount = useMemo(
    () => issues.filter((issue) => normalizeSeverityClass(getValue(issue, "Severity", "severity")) === "warning").length,
    [issues]
  );
  const errorCount = issues.length - warningCount;
  const validationStepLabel = getSectionStepLabel(context.sectionStep);
  const expectedRecords = (getValue(context.sectionStep, "ExpectedRecords", "expectedRecords") || []).join(", ") || "Not specified";

  return (
    <div className="resource-wizard__validation-step">
      <header className="resource-wizard__validation-header">
        <div>
          <h3>{validationStepLabel}</h3>
          <p>{getValue(context.sectionStep, "Purpose", "purpose") || "Run the configured validation checks for this setup step."}</p>
        </div>
        <div className="resource-wizard__validation-counts" aria-label="Validation result counts">
          <span className="resource-wizard__validation-count resource-wizard__validation-count--error">{errorCount} errors</span>
          <span className="resource-wizard__validation-count resource-wizard__validation-count--warning">{warningCount} warnings</span>
        </div>
      </header>

      <div className="resource-wizard__pattern-facts">
        <div className="resource-wizard__info-pair">
          <span>Provider</span>
          <strong>{context.productResourceDefaults?.providerName || "Not set"}</strong>
        </div>
        <div className="resource-wizard__info-pair">
          <span>Expected records</span>
          <strong>{expectedRecords}</strong>
        </div>
      </div>

      <div className="resource-wizard__validation-results">
        {issues.length === 0 ? (
          <div className={`resource-wizard__validation-empty${hasValidated ? " resource-wizard__validation-empty--success" : ""}`}>
            {hasValidated
              ? `${validationStepLabel} validation successful. No issues found.`
              : `${validationStepLabel} validation has not run yet.`}
          </div>
        ) : issues.map((issue, index) => {
          const severityClass = normalizeSeverityClass(getValue(issue, "Severity", "severity"));
          return (
            <div
              className={`resource-wizard__validation-issue resource-wizard__validation-issue--${severityClass}`}
              key={`${severityClass}-${getValue(issue, "StepKey", "stepKey")}-${getValue(issue, "SectionStepKey", "sectionStepKey")}-${issueMessage(issue)}-${index}`}
            >
              <strong>{severityClass === "warning" ? "Warning" : "Error"}</strong>
              <span>{issueMessage(issue)}</span>
            </div>
          );
        })}
      </div>

      <div className="resource-wizard__linked-step-actions">
        <div />
        <div className="resource-wizard__linked-step-actions-right">
          <ButtonControl
            type="standard-small"
            className="resource-wizard__button resource-wizard__button--secondary"
            onClick={onSave}
            disabled={context.readOnly || context.saving || validating}
            loading={validating}
          >
            Validate
          </ButtonControl>
          <ButtonControl
            type="ok-small"
            className="resource-wizard__button resource-wizard__button--primary"
            onClick={onContinue}
            disabled={context.readOnly || context.saving || validating}
            loading={validating}
          >
            Validate & Continue
          </ButtonControl>
        </div>
      </div>
    </div>
  );
};

const USAGE_ESTIMATE_STATE_FIELD = "__usageEstimateState";
const TRANSACTION_USAGE_UNIT_MODE = 5;

const getStoredUsageEstimateState = (values = {}) => getValue(values, USAGE_ESTIMATE_STATE_FIELD, "usageEstimateState") || {};

const parseEstimateParameters = (values = {}) => {
  const raw = getValue(values, "EstimateParameters", "estimateParameters", "EstimateParametersJson", "estimateParametersJson");
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const formatMoney = (value, currency = "USD") => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return `${currency} 0.00`;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD"
  }).format(numeric);
};

const usageLabel = (prefix, unitLabel) => `${prefix} ${String(unitLabel || "usage units").trim()}`.trim();

const billingPeriodOptions = [
  { value: 1, label: "Monthly" },
  { value: 2, label: "Annual" }
];

const UNLIMITED_ALLOWANCE_VALUE = 2147483647;

const normalizeSupportedBillingPeriod = (value) => (Number(value) === 2 ? 2 : 1);

const normalizePositiveNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
};

const isUnlimitedValue = (value) => {
  const numeric = Number(value);
  return value === Number.POSITIVE_INFINITY
    || String(value || "").trim().toLowerCase() === "unlimited"
    || (Number.isFinite(numeric) && numeric >= UNLIMITED_ALLOWANCE_VALUE);
};

const findAppliedClientOffer = (wizard, configuration, estimate) => {
  const clientOffers = getValue(wizard, "ClientProductOffers", "clientProductOffers") || [];
  const clientProductOfferId = getValue(estimate, "ClientProductOfferId", "clientProductOfferId")
    || getValue(configuration, "AllowanceClientProductOfferId", "allowanceClientProductOfferId");
  const productOfferKey = getValue(estimate, "ProductOfferKey", "productOfferKey")
    || getValue(configuration, "AllowanceProductOfferKey", "allowanceProductOfferKey");
  const productOfferSku = getValue(estimate, "ProductOfferSku", "productOfferSku")
    || getValue(configuration, "AllowanceProductOfferSku", "allowanceProductOfferSku");
  return clientOffers.find((offer) => getValue(offer, "_id", "id") === clientProductOfferId)
    || clientOffers.find((offer) =>
      getValue(offer, "ProductOfferKey", "productOfferKey") === productOfferKey
      && (!productOfferSku || getValue(offer, "Sku", "sku") === productOfferSku))
    || null;
};

const findAppliedGrantSnapshot = (wizard, configuration, estimate) => {
  const appliedOffer = findAppliedClientOffer(wizard, configuration, estimate);
  const grants = getValue(appliedOffer, "ResourceGrantsSnapshot", "resourceGrantsSnapshot") || [];
  const grantResourceKey = getValue(estimate, "GrantProductResourceKey", "grantProductResourceKey")
    || getValue(configuration, "AllowanceGrantProductResourceKey", "allowanceGrantProductResourceKey")
    || getValue(wizard, "ProductResourceKey", "productResourceKey");
  return grants.find((grant) =>
    getValue(grant, "ProductResourceKey", "productResourceKey") === grantResourceKey)
    || null;
};

const isUnlimitedAppliedGrant = (wizard, configuration, estimate) => {
  const appliedGrant = findAppliedGrantSnapshot(wizard, configuration, estimate);
  if (isUnlimitedValue(getValue(appliedGrant, "AllowedResourceConfigurationCount", "allowedResourceConfigurationCount"))
    || isUnlimitedValue(getValue(appliedGrant, "IncludedUsageUnits", "includedUsageUnits"))) {
    return true;
  }

  const appliedOffer = findAppliedClientOffer(wizard, configuration, estimate);
  const offerKind = getValue(appliedOffer, "OfferKind", "offerKind");
  const offerText = [
    offerKind,
    getValue(appliedOffer, "DisplayName", "displayName"),
    getValue(appliedOffer, "ProductOfferKey", "productOfferKey"),
    getValue(configuration, "AllowanceProductOfferKey", "allowanceProductOfferKey")
  ].filter(Boolean).join(" ").toLowerCase();
  return Number(offerKind) === 3 || offerText.includes("unlimited");
};

const resolveDefaultBillingPeriod = (wizard, configuration, estimate) => {
  if (isUnlimitedAppliedGrant(wizard, configuration, estimate)) return 1;

  const estimatePeriod = getValue(estimate, "BillingPeriod", "billingPeriod");
  if (Number(estimatePeriod) === 1 || Number(estimatePeriod) === 2) return Number(estimatePeriod);

  const appliedGrant = findAppliedGrantSnapshot(wizard, configuration, estimate);
  const grantPeriod = getValue(appliedGrant, "BillingPeriod", "billingPeriod");
  if (Number(grantPeriod) === 1 || Number(grantPeriod) === 2) return Number(grantPeriod);

  const appliedOffer = findAppliedClientOffer(wizard, configuration, estimate);
  const priceSnapshot = getValue(appliedOffer, "PriceSnapshot", "priceSnapshot") || {};
  const offerPeriod = getValue(priceSnapshot, "BillingPeriod", "billingPeriod")
    || getValue(appliedOffer, "BillingPeriod", "billingPeriod");
  return normalizeSupportedBillingPeriod(offerPeriod);
};

const getAppliedGrantDisplayName = (wizard, configuration, estimate) => {
  const appliedOffer = findAppliedClientOffer(wizard, configuration, estimate);
  return getValue(appliedOffer, "DisplayName", "displayName")
    || getValue(estimate, "ProductOfferDisplayName", "productOfferDisplayName")
    || getValue(configuration, "AllowanceProductOfferDisplayName", "allowanceProductOfferDisplayName")
    || getValue(estimate, "ProductOfferKey", "productOfferKey")
    || getValue(configuration, "AllowanceProductOfferKey", "allowanceProductOfferKey")
    || "Unassigned";
};

const resolveUsageUnitMode = (wizard, configuration, estimate) => {
  const estimateMode = Number(getValue(estimate, "UsageUnitMode", "usageUnitMode"));
  if (estimateMode) return estimateMode;

  const appliedGrant = findAppliedGrantSnapshot(wizard, configuration, estimate);
  const grantMode = Number(getValue(appliedGrant, "UsageUnitMode", "usageUnitMode"));
  if (grantMode) return grantMode;

  return 0;
};

export const UsageEstimateStep = ({
  context,
  onSave,
  registerStepController
}) => {
  const resourceDomainContext = useResourceDomainContext();
  const managerRef = useRef(resourceDomainContext?.managers?.resourceUsageEstimate);
  const coreStateRef = useRef(resourceDomainContext?.coreState);
  const contextRef = useRef(context);
  const resourceConfiguration = getValue(context.wizard, "ResourceConfiguration", "resourceConfiguration") || {};
  const storedState = getStoredUsageEstimateState(context.values);
  const existingSnapshot = storedState.estimateSnapshot
    || getValue(context.values, "UsageEstimateSnapshot", "usageEstimateSnapshot")
    || getValue(resourceConfiguration, "UsageEstimateSnapshot", "usageEstimateSnapshot");
  const isUnlimitedGrant = isUnlimitedAppliedGrant(context.wizard, resourceConfiguration, existingSnapshot);
  const initialEstimateParameters = parseEstimateParameters(context.values);
  const defaultBillingPeriod = resolveDefaultBillingPeriod(context.wizard, resourceConfiguration, existingSnapshot);
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState(defaultBillingPeriod);
  const [estimatedMonthlyTransactions, setEstimatedMonthlyTransactions] = useState(
    normalizePositiveNumber(getValue(initialEstimateParameters, "EstimatedMonthlyTransactions", "estimatedMonthlyTransactions", "MonthlyTransactions", "monthlyTransactions"))
  );
  const [estimate, setEstimate] = useState(existingSnapshot || null);
  const [warnings, setWarnings] = useState(() => getValue(existingSnapshot, "Warnings", "warnings") || []);
  const [estimating, setEstimating] = useState(false);
  const estimatingRef = useRef(false);

  useEffect(() => {
    managerRef.current = resourceDomainContext?.managers?.resourceUsageEstimate;
    coreStateRef.current = resourceDomainContext?.coreState;
  }, [resourceDomainContext?.coreState, resourceDomainContext?.managers?.resourceUsageEstimate]);

  useEffect(() => {
    contextRef.current = context;
  }, [context]);

  const calculateEstimate = useCallback(async (values) => {
    const currentContext = contextRef.current;
    const manager = managerRef.current;
    if (!manager?.RunOperation) throw new Error("Resource usage estimate manager is not available.");

    setEstimating(true);
    estimatingRef.current = true;
    try {
      const wizard = currentContext.wizard;
      const configuration = getValue(wizard, "ResourceConfiguration", "resourceConfiguration") || {};
      const productResource = getValue(wizard, "ProductResource", "productResource") || {};
      const estimateParameters = {
        ...(parseEstimateParameters(values)),
        EstimatedMonthlyTransactions: normalizePositiveNumber(estimatedMonthlyTransactions)
      };
      const payload = {
        ClientResourceId: getValue(wizard, "ClientResourceId", "clientResourceId") || "",
        ResourceConfigurationId: getValue(wizard, "ResourceConfigurationId", "resourceConfigurationId")
          || getValue(configuration, "_id", "id")
          || "",
        ProductResourceKey: getValue(wizard, "ProductResourceKey", "productResourceKey")
          || getValue(productResource, "ProductResourceKey", "productResourceKey")
          || getValue(configuration, "ProductResourceKey", "productResourceKey")
          || "",
        SelectedBillingPeriod: isUnlimitedAppliedGrant(wizard, configuration, estimate) ? 1 : (Number(selectedBillingPeriod) || 0),
        EstimateParametersJson: JSON.stringify(estimateParameters)
      };
      const result = await manager.RunOperation({
        operationName: Operations.ResourceUsageEstimateItem.CalculateResourceUsageEstimate,
        item: payload,
        queryParams: buildResourceOperationQueryParams({
          wizard,
          item: payload,
          resourceConfiguration: configuration
        }),
        isItem: true,
        isWarning: true,
        supressAllErrors: false
      });
      if (!result) throw new Error("Resource usage estimate did not return a response. Sign in again and retry.");
      const operationResult = getOperationResult(result);
      const failed = getValue(result, "Success", "success") === false
        || getValue(operationResult, "Success", "success") === false;
      if (failed) throw buildServerFailureError(operationResult || result, "Resource usage estimate failed.", coreStateRef.current);

      const item = getOperationItem(result) || operationResult || result;
      const snapshot = getValue(item, "EstimateSnapshot", "estimateSnapshot") || item;
      const nextWarnings = getValue(snapshot, "Warnings", "warnings") || getValue(item, "Warnings", "warnings") || [];
      setEstimate(snapshot);
      setWarnings(nextWarnings);
      return {
        ...(values || {}),
        EstimateParameters: estimateParameters,
        UsageEstimateSnapshot: snapshot,
        [USAGE_ESTIMATE_STATE_FIELD]: {
          hasRun: true,
          estimateSnapshot: snapshot,
          warnings: nextWarnings
        }
      };
    } finally {
      estimatingRef.current = false;
      setEstimating(false);
    }
  }, [estimate, estimatedMonthlyTransactions, selectedBillingPeriod]);

  useEffect(() => {
    if (!registerStepController) return undefined;
    registerStepController({
      skipStepInputValidation: true,
      get isBusy() {
        return Boolean(estimatingRef.current);
      },
      prepareSave: calculateEstimate,
      prepareContinue: calculateEstimate
    });
    return () => registerStepController(null);
  }, [calculateEstimate, registerStepController]);

  const currency = getValue(estimate, "CurrencyCode", "currencyCode") || "USD";
  const billingPeriod = isUnlimitedGrant ? 1 : (getValue(estimate, "BillingPeriod", "billingPeriod") || selectedBillingPeriod);
  const calculationLines = getValue(estimate, "CalculationLines", "calculationLines") || [];
  const appliedGrantDisplayName = getAppliedGrantDisplayName(context.wizard, resourceConfiguration, estimate);
  const billingPeriodLabel = billingPeriodOptions.find((x) => x.value === Number(billingPeriod))?.label || "period";
  const usageUnitMode = resolveUsageUnitMode(context.wizard, resourceConfiguration, estimate);
  const isTransactionalEstimate = usageUnitMode === TRANSACTION_USAGE_UNIT_MODE;
  const availableUnits = getValue(estimate, "AvailableUnits", "availableUnits");
  const isUnlimitedEstimate = isUnlimitedGrant || isUnlimitedValue(availableUnits);
  const oneTimeAmount = Number(getValue(estimate, "OneTimeAmount", "oneTimeAmount")) || 0;
  const periodAmount = Number(getValue(estimate, "UsageAmount", "usageAmount")) || 0;
  const showCalculationLines = calculationLines.length > 0
    && (!isUnlimitedEstimate || oneTimeAmount !== 0 || periodAmount !== 0);
  const oneYearTotal = oneTimeAmount + (Number(billingPeriod) === 1 ? periodAmount * 12 : periodAmount);
  const estimateOverviewRows = [
    ["Resource", getValue(context.wizard, "ResourceName", "resourceName") || "Resource"],
    ["Applied grant", appliedGrantDisplayName]
  ];
  const estimateUnitLabel = getValue(estimate, "UnitLabel", "unitLabel") || "usage units";
  const estimateSummaryGroups = [
    [
      ["Usage unit", estimateUnitLabel],
      [usageLabel("Estimated", estimateUnitLabel), `${formatCount(getValue(estimate, "EstimatedUnits", "estimatedUnits"))} ${estimateUnitLabel}`.trim()]
    ],
    [
      [usageLabel("Included", estimateUnitLabel), formatCount(getValue(estimate, "IncludedUnits", "includedUnits") ?? availableUnits)],
      [usageLabel("Billable", estimateUnitLabel), formatCount(getValue(estimate, "BillableUnits", "billableUnits"))]
    ],
    [
      [usageLabel("Remaining included", estimateUnitLabel), formatCount(getValue(estimate, "RemainingUnits", "remainingUnits"))],
      ["Unit rate", isUnlimitedEstimate ? "Included" : formatMoney(getValue(estimate, "UnitRate", "unitRate"), currency)]
    ],
    [
      ["One-time payment", formatMoney(oneTimeAmount, currency)],
      ["Period estimate", `${formatMoney(periodAmount, currency)} / ${billingPeriodLabel}`]
    ],
    [
      ["First year estimate", formatMoney(oneYearTotal, currency)]
    ]
  ];

  return (
    <div className="resource-wizard__usage-estimate-step">
      <header className="resource-wizard__validation-header">
        <div>
          <h3>Usage Estimate</h3>
          <p>Calculate expected usage against the applied Product Offer grant.</p>
        </div>
      </header>

      <div className="resource-wizard__usage-estimate-grid">
        <section className="resource-wizard__usage-estimate-panel">
          <h4>Estimate Overview</h4>
          {!isUnlimitedGrant && (
            <div className="resource-wizard__usage-estimate-billing">
              <FieldControl
                fieldMetadata={{
                  FieldType: SystemTypes.UIFieldType.SelectList,
                  FieldName: "SelectedBillingPeriod",
                  DisplayName: "Billing Period",
                  Description: "Choose the billing period for this estimate.",
                  DataJson: billingPeriodOptions.map((option) => ({ Text: option.label, Value: option.value }))
                }}
                value={selectedBillingPeriod}
                onChange={(nextValue) => setSelectedBillingPeriod(normalizeSupportedBillingPeriod(nextValue))}
                disabled={context.readOnly || estimating}
              />
            </div>
          )}
          {isTransactionalEstimate && (
            <div className="resource-wizard__usage-estimate-billing">
              <FieldControl
                fieldMetadata={{
                  FieldType: SystemTypes.UIFieldType.TextBox,
                  FieldName: "EstimatedMonthlyTransactions",
                  DisplayName: "Estimated Monthly Transactions",
                  Description: "Enter the expected transaction count per month for this estimate."
                }}
                value={estimatedMonthlyTransactions || ""}
                onChange={(nextValue) => setEstimatedMonthlyTransactions(normalizePositiveNumber(nextValue))}
                disabled={context.readOnly || estimating}
              />
            </div>
          )}
          <div className="resource-wizard__usage-estimate-input-list">
            {estimateOverviewRows.map(([label, value]) => (
              <div className="resource-wizard__usage-estimate-input-row" key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </section>

        <section className="resource-wizard__usage-estimate-panel">
          <h4>Estimate</h4>
          {estimate ? (
            <>
              <div className="resource-wizard__usage-estimate-summary-list">
                {estimateSummaryGroups.map((group) => (
                  <div
                    className={`resource-wizard__usage-estimate-summary-row${group.length === 1 ? " resource-wizard__usage-estimate-summary-row--single" : ""}`}
                    key={group.map(([label]) => label).join("-")}
                  >
                    {group.map(([label, value]) => (
                      <div className="resource-wizard__usage-estimate-summary-item" key={label}>
                        <span>{label}</span>
                        <strong>{value}</strong>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {showCalculationLines && (
                <div className="resource-wizard__usage-lines">
                  {calculationLines.map((line, index) => (
                    <div className="resource-wizard__usage-line" key={`${getValue(line, "Label", "label")}-${index}`}>
                      <strong>{getValue(line, "Label", "label")}</strong>
                      <span>{getValue(line, "Description", "description")}</span>
                      <b>{formatMoney(getValue(line, "Amount", "amount"), currency)}</b>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="resource-wizard__validation-empty">Usage estimate has not run yet.</div>
          )}
          {warnings.length > 0 && (
            <div className="resource-wizard__usage-warnings">
              {warnings.map((warning, index) => (
                <div className="resource-wizard__validation-issue resource-wizard__validation-issue--warning" key={`${warning}-${index}`}>
                  <strong>Warning</strong>
                  <span>{warning}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className="resource-wizard__linked-step-actions">
        <div />
        <div className="resource-wizard__linked-step-actions-right">
          <ButtonControl
            type="standard-small"
            className="resource-wizard__button resource-wizard__button--secondary"
            onClick={onSave}
            disabled={context.readOnly || context.saving || estimating}
            loading={estimating}
          >
            Calculate
          </ButtonControl>
        </div>
      </div>
    </div>
  );
};

const getSummaryFacts = (summary) => getValue(summary, "Facts", "facts") || [];
const getSummarySections = (summary) => getValue(summary, "Sections", "sections") || [];

const isEdiWizardContext = (wizard = {}) => {
  const productResource = getValue(wizard, "ProductResource", "productResource") || {};
  const configuration = getValue(wizard, "ResourceConfiguration", "resourceConfiguration") || {};
  const productKey = getValue(productResource, "ProductKey", "productKey")
    || getValue(configuration, "ProductKey", "productKey")
    || getValue(wizard, "ProductKey", "productKey")
    || "";
  const setupPathKey = getValue(productResource, "SetupPathKey", "setupPathKey")
    || getValue(configuration, "SetupPathKey", "setupPathKey")
    || getValue(wizard, "SetupPathKey", "setupPathKey")
    || "";
  const productResourceKey = getValue(productResource, "ProductResourceKey", "productResourceKey")
    || getValue(configuration, "ProductResourceKey", "productResourceKey")
    || getValue(wizard, "ProductResourceKey", "productResourceKey")
    || "";

  const normalizedProductKey = String(productKey).toLowerCase();
  const normalizedSetupPathKey = String(setupPathKey).toLowerCase();
  const resourceKey = String(productResourceKey);

  return normalizedProductKey === "inboundedi"
    || normalizedProductKey === "outboundedi"
    || normalizedSetupPathKey === "inboundedisetup"
    || normalizedSetupPathKey === "outboundedisetup"
    || /inboundedi$/i.test(resourceKey)
    || /outboundedi$/i.test(resourceKey);
};

const buildResourceManageTarget = (context) => {
  const wizard = context?.wizard || {};
  const configuration = getValue(wizard, "ResourceConfiguration", "resourceConfiguration") || {};
  const clientResourceId = getValue(wizard, "ClientResourceId", "clientResourceId")
    || getValue(configuration, "ClientResourceId", "clientResourceId")
    || "";
  const resourceConfigurationId = getValue(wizard, "ResourceConfigurationId", "resourceConfigurationId")
    || getValue(configuration, "_id", "id")
    || "";

  if (!clientResourceId) return RESOURCES_BASE_PATH;

  if (resourceConfigurationId && isEdiWizardContext(wizard)) {
    return buildResourceConfigurationWorkspaceScreenPath(clientResourceId, resourceConfigurationId, "manage");
  }

  return resourceConfigurationId
    ? buildResourceConfigurationWorkspacePath(clientResourceId, resourceConfigurationId)
    : buildResourceConfigurationManagePath(clientResourceId);
};

const isTechnicalReviewFact = (fact) => {
  const label = String(getValue(fact, "Label", "label") || "").trim().toLowerCase();
  return label === "object key" || label === "target key" || label.endsWith(" key");
};

const normalizeReviewSections = (summary) =>
  getSummarySections(summary)
    .filter((section) => !stepKeysEqual(getValue(section, "SectionKey", "sectionKey"), "source-targets"))
    .map((section) => {
      const sectionKey = getValue(section, "SectionKey", "sectionKey");
      const isSourceObjectsSection = stepKeysEqual(sectionKey, "source-objects");
      return {
        ...section,
        Records: (getValue(section, "Records", "records") || []).map((record) => ({
          ...record,
          Status: isSourceObjectsSection ? "" : getValue(record, "Status", "status"),
          Facts: (getValue(record, "Facts", "facts") || []).filter((fact) => !isTechnicalReviewFact(fact))
        }))
      };
    });

const hasEstimateSnapshot = (snapshot) =>
  Boolean(snapshot && (
    getValue(snapshot, "ResourceConfigurationId", "resourceConfigurationId")
    || getValue(snapshot, "ProductOfferKey", "productOfferKey")
    || Number(getValue(snapshot, "EstimatedUnits", "estimatedUnits")) > 0
  ));

const firstYearEstimateAmount = (snapshot) => {
  const billingPeriod = Number(getValue(snapshot, "BillingPeriod", "billingPeriod")) || 1;
  const oneTimeAmount = Number(getValue(snapshot, "OneTimeAmount", "oneTimeAmount")) || 0;
  const periodAmount = Number(getValue(snapshot, "UsageAmount", "usageAmount")) || 0;
  return oneTimeAmount + (billingPeriod === 1 ? periodAmount * 12 : periodAmount);
};

const ReviewEstimateSummary = ({ snapshot }) => {
  if (!hasEstimateSnapshot(snapshot)) {
    return (
      <section className="resource-wizard__review-summary-section">
        <h4>Usage Estimate</h4>
        <div className="resource-wizard__validation-empty">No usage estimate has been saved yet.</div>
      </section>
    );
  }

  const currency = getValue(snapshot, "CurrencyCode", "currencyCode") || "USD";
  const unitLabel = getValue(snapshot, "UnitLabel", "unitLabel") || "usage units";
  const rows = [
    ["Usage unit", unitLabel],
    [usageLabel("Estimated", unitLabel), `${formatCount(getValue(snapshot, "EstimatedUnits", "estimatedUnits"))} ${unitLabel}`.trim()],
    [usageLabel("Included", unitLabel), formatCount(getValue(snapshot, "IncludedUnits", "includedUnits") ?? getValue(snapshot, "AvailableUnits", "availableUnits"))],
    [usageLabel("Billable", unitLabel), formatCount(getValue(snapshot, "BillableUnits", "billableUnits"))],
    [usageLabel("Remaining included", unitLabel), formatCount(getValue(snapshot, "RemainingUnits", "remainingUnits"))],
    ["First year estimate", formatMoney(firstYearEstimateAmount(snapshot), currency)]
  ];

  return (
    <section className="resource-wizard__review-summary-section">
      <h4>Usage Estimate</h4>
      <div className="resource-wizard__review-summary-facts">
        {rows.map(([label, value]) => (
          <div className="resource-wizard__info-pair" key={label}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>
    </section>
  );
};

const ReviewSummaryRecords = ({ section }) => {
  const records = getValue(section, "Records", "records") || [];
  const sectionKey = getValue(section, "SectionKey", "sectionKey") || getValue(section, "Title", "title") || "section";
  const [visibleCount, setVisibleCount] = useState(REVIEW_RECORD_BATCH_SIZE);
  const visibleRecords = records.slice(0, visibleCount);
  const remainingCount = Math.max(records.length - visibleCount, 0);
  const nextBatchCount = Math.min(REVIEW_RECORD_BATCH_SIZE, remainingCount);

  useEffect(() => {
    setVisibleCount(REVIEW_RECORD_BATCH_SIZE);
  }, [records.length, sectionKey]);

  return (
    <section className="resource-wizard__review-summary-section">
      <div className="resource-wizard__review-summary-section-header">
        <h4>{getValue(section, "Title", "title") || "Setup section"}</h4>
        {getValue(section, "Description", "description") ? <p>{getValue(section, "Description", "description")}</p> : null}
      </div>
      <div className="resource-wizard__review-summary-records">
        {records.length === 0 ? (
          <div className="resource-wizard__validation-empty">No active records selected.</div>
        ) : visibleRecords.map((record, index) => {
          const facts = getValue(record, "Facts", "facts") || [];
          const entityObjectsFact = facts.find(isEntityObjectsFact);
          const displayFacts = facts.filter((fact) => !isEntityObjectsFact(fact));
          const secondaryText = getValue(record, "SecondaryText", "secondaryText");
          return (
            <article className="resource-wizard__review-summary-record" key={`${getValue(record, "DisplayName", "displayName")}-${index}`}>
              <div className="resource-wizard__review-summary-record-header">
                <div>
                  <strong>{getValue(record, "DisplayName", "displayName") || "Record"}</strong>
                  {secondaryText ? (
                    <span className="resource-wizard__review-summary-record-subtitle" title={secondaryText}>
                      {secondaryText}
                    </span>
                  ) : null}
                </div>
                {getValue(record, "Status", "status") ? (
                  <span className="resource-wizard__review-summary-record-status">{getValue(record, "Status", "status")}</span>
                ) : null}
              </div>
              {displayFacts.length > 0 ? (
                <div className="resource-wizard__review-summary-record-facts">
                  {displayFacts.map((fact) => (
                    <ReviewSummaryFact fact={fact} key={getValue(fact, "Label", "label")} />
                  ))}
                </div>
              ) : null}
              {entityObjectsFact ? <EntityObjectCardList fact={entityObjectsFact} /> : null}
            </article>
          );
        })}
        {remainingCount > 0 ? (
          <ButtonControl
            type="standard-small"
            className="resource-wizard__button resource-wizard__button--secondary resource-wizard__review-summary-show-more"
            onClick={() => setVisibleCount((current) => current + REVIEW_RECORD_BATCH_SIZE)}
          >
            Show {nextBatchCount} more
          </ButtonControl>
        ) : null}
      </div>
    </section>
  );
};

const ReviewSummaryContent = ({ context, summary, isCompleteScreen }) => {
  const navigate = useNavigate();
  const facts = getSummaryFacts(summary);
  const sections = normalizeReviewSections(summary);
  const estimateSnapshot = getValue(summary, "UsageEstimateSnapshot", "usageEstimateSnapshot")
    || getValue(context.wizard, "UsageEstimateSnapshot", "usageEstimateSnapshot")
    || {};
  const manageTarget = buildResourceManageTarget(context);

  return (
    <div className="resource-wizard__review-summary">
      {isCompleteScreen ? (
        <section className="resource-wizard__review-summary-complete">
          <div>
            <h3>Setup complete</h3>
            <p>This Resource Configuration is active. Review the setup summary below, then use Resource Management to manage this resource.</p>
          </div>
          <ButtonControl
            type="ok"
            className="resource-wizard__button resource-wizard__button--primary"
            data-target={manageTarget}
            onClick={() => navigate(manageTarget)}
          >
            Manage Resource
          </ButtonControl>
        </section>
      ) : (
        <header className="resource-wizard__review-summary-header">
          <div>
            <h3>Review</h3>
            <p>Confirm the applied allowance, usage estimate, and active setup records before completing this Resource Configuration.</p>
          </div>
        </header>
      )}

      {facts.length > 0 ? (
        <section className="resource-wizard__review-summary-section">
          <h4>Configuration</h4>
          <div className="resource-wizard__review-summary-facts">
            {facts.map((fact) => (
              <ReviewSummaryFact fact={fact} key={getValue(fact, "Label", "label")} />
            ))}
          </div>
        </section>
      ) : null}

      <ReviewEstimateSummary snapshot={estimateSnapshot} />

      {sections.map((section) => (
        <ReviewSummaryRecords
          key={getValue(section, "SectionKey", "sectionKey") || getValue(section, "Title", "title")}
          section={section}
        />
      ))}
    </div>
  );
};

const ReviewSummaryStep = ({
  context,
  registerStepController,
  isCompleteScreen = false
}) => {
  const resourceDomainContext = useResourceDomainContext();
  const managerRef = useRef(resourceDomainContext?.managers?.resourceConfigurationReviewSummary);
  const coreStateRef = useRef(resourceDomainContext?.coreState);
  const loadKeyRef = useRef("");
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const reviewManagerReady = Boolean(resourceDomainContext?.managers?.resourceConfigurationReviewSummary?.RunOperation);
  const configuration = getValue(context.wizard, "ResourceConfiguration", "resourceConfiguration") || {};
  const clientResourceId = getValue(context.wizard, "ClientResourceId", "clientResourceId")
    || getValue(configuration, "ClientResourceId", "clientResourceId")
    || "";
  const resourceConfigurationId = getValue(context.wizard, "ResourceConfigurationId", "resourceConfigurationId")
    || getValue(configuration, "_id", "id")
    || "";
  const productResourceKey = getValue(context.wizard, "ProductResourceKey", "productResourceKey")
    || getValue(configuration, "ProductResourceKey", "productResourceKey")
    || "";
  const configurationStatus = getValue(configuration, "ConfigurationStatus", "configurationStatus") || "";
  const loadKey = `${clientResourceId}|${resourceConfigurationId}|${productResourceKey}|${configurationStatus}|${isCompleteScreen ? "complete" : "review"}`;

  useEffect(() => {
    managerRef.current = resourceDomainContext?.managers?.resourceConfigurationReviewSummary;
    coreStateRef.current = resourceDomainContext?.coreState;
  }, [resourceDomainContext?.coreState, resourceDomainContext?.managers?.resourceConfigurationReviewSummary]);

  useEffect(() => {
    if (!registerStepController) return undefined;
    registerStepController({
      skipStepInputValidation: true,
      prepareSave: async (values) => values || {},
      prepareContinue: async (values) => values || {}
    });
    return () => registerStepController(null);
  }, [registerStepController]);

  useEffect(() => {
    const manager = managerRef.current;
    if (!reviewManagerReady || !manager?.RunOperation || !resourceConfigurationId || loadKeyRef.current === loadKey) {
      return undefined;
    }

    let cancelled = false;
    loadKeyRef.current = loadKey;
    setLoadingSummary(true);
    setSummaryError("");
    const payload = {
      ClientResourceId: clientResourceId,
      ResourceConfigurationId: resourceConfigurationId,
      ProductResourceKey: productResourceKey
    };
    manager.RunOperation({
      operationName: Operations.ResourceConfigurationReviewSummaryItem.LoadResourceConfigurationReviewSummary,
      item: payload,
      queryParams: buildResourceOperationQueryParams({
        item: payload,
        clientResourceId,
        resourceConfigurationId
      }),
      isItem: true,
      isWarning: true,
      supressAllErrors: false
    }).then((result) => {
      if (cancelled) return;
      if (!result) throw new Error("Resource review summary did not return a response. Sign in again and retry.");
      const operationResult = getOperationResult(result);
      const failed = getValue(result, "Success", "success") === false
        || getValue(operationResult, "Success", "success") === false;
      if (failed) throw buildServerFailureError(operationResult || result, "Resource review summary failed.", coreStateRef.current);
      setSummary(getOperationItem(result) || operationResult || result);
    }).catch((ex) => {
      if (cancelled) return;
      setSummaryError(getErrorSummary(ex, "Resource review summary could not be loaded."));
    }).finally(() => {
      if (!cancelled) setLoadingSummary(false);
    });

    return () => {
      cancelled = true;
    };
  }, [clientResourceId, loadKey, productResourceKey, resourceConfigurationId, reviewManagerReady]);

  return (
    <div className="resource-wizard__review-summary-step">
      {summaryError ? <div className="resource-wizard__alert">{summaryError}</div> : null}
      {loadingSummary && !summary ? (
        <div className="resource-wizard__validation-empty">Loading review summary.</div>
      ) : (
        <ReviewSummaryContent
          context={context}
          summary={summary || {}}
          isCompleteScreen={isCompleteScreen}
        />
      )}
    </div>
  );
};

export const ReviewStep = (props) => <ReviewSummaryStep {...props} />;

export const CompleteStep = (props) => <ReviewSummaryStep {...props} isCompleteScreen />;
